import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { reaction, action, observable } from 'mobx';
import { parse as qs_parse } from 'query-string';
import { authorize } from 'utils/auth';
import { INTERNAL_ERROR, BAD_REQUEST } from 'config/constants';
import { getIndex } from 'utils/helpers';
import { getYesNoValue } from 'api/helpers';
import { FormattedMessage } from 'ui/i18n';

import {
	render_date,
	render_amount,
	render_holdInvWeeks,
	render_yes_no,
	render_editable_number,
	render_quantity,
} from 'utils';

import {
	DataTable,
	Loader,
	ItemPreview,
	ItemImage,
	PageHeader,
	ItemContentHeader,
	GoBack,
	ErrorLoadingContent,
	Alert,
	PromotionBreadcrumb,
} from 'ui/components';

import './ItemDetails.scss';

function getColumns(onChangeQty, onBlurHandler) {
	return [
		{
			prop: 'list_price',
			title: <FormattedMessage id="itemsTable.listColumn" />,
			align: 'right',
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'quantity',
			title: <FormattedMessage id="itemsTable.qtyColumn" />,
			align: 'center',
			render(row) {
				return render_quantity(row[this.prop]);
			},
		},
		{
			prop: 'ta',
			title: <FormattedMessage id="itemsTable.TAColumn" />,
			render(row) {
				return `${row[this.prop]}%`;
			},
			align: 'right',
		},
		{
			prop: 'net_price',
			title: <FormattedMessage id="itemsTable.netColumn" />,
			align: 'right',
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'est_ship_date',
			title: <FormattedMessage id="itemsTable.estShipColumn" />,
			align: 'center',
			render(row) {
				const value = row[this.prop];
				return render_date(value);
			},
		},
		{
			prop: 'invoice_hold_weeks',
			title: <FormattedMessage id="itemsTable.numberHoldInvWksColumn" />,
			align: 'center',
			render(row) {
				return render_holdInvWeeks(row, row[this.prop]);
			},
		},
		{
			prop: 'is_drop_ship',
			title: <FormattedMessage id="itemsTable.dropShipColumn" />,
			align: 'center',
			render(row) {
				return render_yes_no(getYesNoValue(row[this.prop]));
			},
		},
		{
			prop: 'id',
			title: <FormattedMessage id="itemsTable.addToCartQtyColumn" />,
			align: 'center',
			render(row) {
				return render_editable_number(
					row,
					null,
					false,
					onChangeQty,
					onBlurHandler
				);
			},
		},
	];
}

@authorize({ rights: ['GetDetails'] })
@inject('ItemsState', 'UIState', 'CartState', 'CartItemsState')
@observer
export default class ItemDetailsItemPage extends Component {
	static propTypes = {
		ItemsState: PropTypes.object.isRequired,
		UIState: PropTypes.object.isRequired,
		CartState: PropTypes.object.isRequired,
		CartItemsState: PropTypes.object.isRequired,
		history: PropTypes.object.isRequired,
	};

	componentWillMount() {
		this.ItemsState.fetchItemDetailsItem(this.urlParams);

		reaction(
			() => this.ItemsState.fetchItemDetailsItem.state.result,
			result =>
				result &&
				this.props.UIState.setSelectedPromotion({
					category_id: result[0].category_id,
					promo_id: result[0].promotion_id,
					country_short_name: result[0].currency.country_short_name,
				})
		);
	}

	componentWillUnmount() {
		this.props.UIState.deselectPromotionInMenu(
			parseInt(this.urlParams.categoryId, 0)
		);
		this.CartItemsState.emptyCurrentCart();
		this.props.CartState.removeAPIError();
	}

	@observable showModal = false;
	ItemsState = this.props.ItemsState;
	CartState = this.props.CartState;
	CartItemsState = this.props.CartItemsState;
	history = this.props.history;

	urlParams = this.getUrlParams();

	getUrlParams() {
		return qs_parse(window.location.search);
	}

	onChangeQty(value, item) {
		if (value) {
			item.quantity_ordered = value;
			item.errorMsgId = this.CartItemsState.getErrorMessageId(
				false,
				this.CartState.getAvailableQuantity(item, false),
				this.CartState.getTotalQuantityFromCart(item),
				item
			);
			this.CartItemsState.addToCurrentCart(item);
		} else {
			this.CartItemsState.deleteFromCart(item);
		}
	}

	onBlurHandler(item) {
		const index = getIndex(
			this.CartItemsState.cartUpdates,
			'number',
			item.number
		);

		if (
			index >= 0 &&
			item.is_configurable &&
			item.quantity_ordered &&
			!item.errorMsgId
		) {
			this.toggleModal(item);
		}
	}

	@action
	toggleModal() {
		this.ItemsState.fetchItemDetailsItem(this.urlParams);
		this.showModal = !this.showModal;
	}

	@action
	handleAddToCart() {
		this.showModal = false;

		if (this.CartItemsState.isCurrentCartEmpty) {
			this.CartItemsState.toggleWarningMessage();
		} else {
			this.CartState.addToCart(this.CartItemsState.cartUpdates).then(() => {
				this.CartItemsState.emptyCurrentCart();

				this.ItemsState.fetchItemDetailsItem(this.urlParams);
			});
		}
	}

	render() {
		const { fetchItemDetailsItem } = this.props.ItemsState;

		return (
			<div className="ItemDetails">
				{fetchItemDetailsItem.handle({
					loading: () => <Loader />,
					rejected: error =>
						<div>
							{error.status === INTERNAL_ERROR || error.status === BAD_REQUEST
								? <ErrorLoadingContent />
								: <div className="ItemDetailsError">
										<PageHeader
											headerText={
												<span>
													<FormattedMessage id="itemDetails.headerText" />
												</span>
											}
										/>
										<Alert
											type="danger"
											msgId={`itemdetails.${error.message}`}
										/>
									</div>}
						</div>,

					resolved: items => {
						const [packDetails] = items;

						return (
							<div className="ItemDetailsItem">
								<PromotionBreadcrumb
									promotionName={packDetails.promotion_name}
									categoryName={packDetails.category_name}
									itemDetails={packDetails}
								/>

								<ItemContentHeader
									handleAddToCart={this.handleAddToCart.bind(this)}
									disableAddToCart={
										this.CartItemsState.hasError ||
										this.CartState.addToCart.state.loading ||
										packDetails.is_preview
									}
								>
									<GoBack
										goBackText="general.continueShopping"
										history={this.history}
									/>
								</ItemContentHeader>

								<div className="ItemDetails-content-item">
									<div className="row">
										<ItemImage
											imageUrl={packDetails.image_url}
											name={packDetails.name}
											itemNumber={this.urlParams.itemNumber}
										/>

										<ItemPreview
											showChooseColor={this.showModal}
											item={packDetails}
											toggleModal={this.toggleModal.bind(this)}
											handleAddToCart={this.handleAddToCart.bind(this)}
										/>

										<div className="col-md-8 col-sm-8 col-xs-8">
											<DataTable
												id="ItemDetailsItem-info-table"
												columns={getColumns(
													this.onChangeQty.bind(this),
													this.onBlurHandler.bind(this)
												)}
												rows={items}
											/>
										</div>
									</div>
								</div>
							</div>
						);
					},
				})}
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/pages/ItemDetailsItem/index.js